import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import services from 'api/services';
import * as passwordActions from 'global/actions/passwordActions';
import { passwordHasKeyword } from 'functions/stringValidation';
import { userRoles } from 'constants/users';
import { passwordRegex } from 'constants/regex';
import { useAppContext } from 'hooks/useContext';
import { useActions } from 'hooks/useActions';
import apiErrors from 'api/apiErrors';
import * as strings from './strings';
import { Container, Wrapper } from 'components/Layout/Layout';
import { ButtonsContainer, RectangleButton } from 'components/Button/RectangleButton';
import { Form } from 'components/FormComponents/FormComponents';
import {
  AuthPageBackgroundImage,
  AuthContainer,
  Heading,
  InputContainer,
  InputField,
  Icon,
  InfoText,
  AuthPageGradient,
  TextContainer,
} from '../LoginPage/styles';

const { AuthService } = services;

export default React.memo(() => {
  const { session } = useParams();

  const { language } = useAppContext();

  const { loading, error, success } = useSelector((state) => state.managePassword);
  const { clearError, clearState, submitPassword } = useActions(passwordActions);

  const [validPassword, setValidPassword] = useState(false);
  const [invalidKeywordsUsed, setInvalidKeywordUsed] = useState(false);
  const [name, setName] = useState('');
  const [communityName, setCommunityName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [nameIsRequired, setNameIsRequired] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  useEffect(() => {
    const getAccountData = async () => {
      try {
        let { user } = await AuthService.getAccount(session);
        if (!user?.role) {
          return setServerErrorMessage('User not found');
        }
        if (user.role === userRoles.CANDIDATE && !user.coderCommunityProfile?.name) {
          setNameIsRequired(true);
        }
        setName(user.name || '');
        setEmail(user.email || '');
        setCommunityName(user.coderCommunityProfile?.name || '');
      } catch (err) {
        if (err.name === 'RESOURCE_NOT_FOUND_ERROR') {
          setServerErrorMessage('The link is invalid. Please make sure you copied the full link.');
        } else {
          setServerErrorMessage(err.message);
        }
      }
    };
    getAccountData();
  }, [session, nameIsRequired]);

  useEffect(() => {
    return () => clearState();
  }, [clearState]);

  useEffect(() => {
    setValidPassword(passwordRegex.test(password));
    setInvalidKeywordUsed(passwordHasKeyword(password, email, name, communityName));
  }, [password, communityName, name, email]);

  return (
    <Wrapper isNewPage>
      <AuthPageBackgroundImage>
        <AuthPageGradient />
      </AuthPageBackgroundImage>
      <Container column useZIndex="1">
        <AuthContainer isSignUpPage>
          <Heading>{strings.header[language]}</Heading>
          <Form
            onChange={() => error && clearError()}
            onSubmit={() => submitPassword(password, communityName, session)}
          >
            {nameIsRequired && (
              <InputContainer hasError={error}>
                <Icon className="fa-solid fa-user" />
                <InputField
                  type="text"
                  value={communityName}
                  disabled={serverErrorMessage}
                  placeholder={strings.namePlaceholder[language]}
                  onChange={(e) => setCommunityName(e.target.value)}
                  onBlur={(e) => setCommunityName(e.target.value.trim())}
                />
              </InputContainer>
            )}
            <InputContainer>
              <Icon className="fa-solid fa-lock" />
              <InputField
                type="password"
                value={password}
                disabled={serverErrorMessage}
                placeholder={strings.passwordPlaceholder[language]}
                onChange={(e) => setPassword(e.target.value)}
              />
            </InputContainer>
            <InputContainer>
              <Icon className="fa-solid fa-lock" />
              <InputField
                type="password"
                value={repeatPassword}
                disabled={serverErrorMessage}
                placeholder={strings.repeatPasswordPlaceholder[language]}
                onChange={(e) => setRepeatPassword(e.target.value)}
              />
            </InputContainer>
            <ButtonsContainer padding="20px 0 0">
              <RectangleButton
                type="submit"
                isAuthButton
                padding="20px 0 0"
                disabled={
                  !password ||
                  !repeatPassword ||
                  error ||
                  !validPassword ||
                  password !== repeatPassword ||
                  invalidKeywordsUsed ||
                  (nameIsRequired && !communityName) ||
                  serverErrorMessage
                }
              >
                {strings.header[language]}
              </RectangleButton>
            </ButtonsContainer>
          </Form>
          {repeatPassword && password !== repeatPassword && (
            <InfoText error={password !== repeatPassword}>Passwords don't match</InfoText>
          )}
          {serverErrorMessage && <InfoText error>{serverErrorMessage}</InfoText>}
          {(error || success) && (
            <InfoText error={error} success={success}>
              {loading ? strings.loadingLabel[language] : ''}
              {apiErrors?.[error?.name]?.[language] ?? error?.[language] ?? error}
              {success ? strings.resetSuccessLabel[language] : ''}
            </InfoText>
          )}
          <TextContainer success={validPassword ? true : false}>
            1. Password must be at least 10 characters long, contain a lowercase letter, one uppercase letter, and one
            digit.
          </TextContainer>
          <TextContainer
            error={invalidKeywordsUsed ? true : false}
            success={invalidKeywordsUsed || !password.length ? false : true}
          >
            2. Your Name and E-mail (username and domain of username@domain.com) should not be part of the password.
          </TextContainer>
          {nameIsRequired && (
            <TextContainer error={!communityName} success={!!communityName?.length}>
              3. A user alias is required.
            </TextContainer>
          )}
        </AuthContainer>
      </Container>
    </Wrapper>
  );
});
